import { type CustomChart } from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"
import { type Atom, useAtomValue } from "jotai"
import { Suspense } from "react"
import { twMerge } from "tailwind-merge"
import { BaseChart } from "./components/chart/BaseChart"
import { ChartHeading } from "./components/chart/ChartHeading"
import { Legend } from "./components/legend/Legend"
import { LegendPlaceholder } from "./components/legend/Placeholder"
import { useSeries } from "./hooks/useSeries"
import { type GranularityType } from "./tt-ui-chart/utils/groupDataBasedOnGranularity"

export type ChartProps = {
  configAtom: Atom<CustomChart>
  showTitle: boolean
  showDescription: boolean
  isVisible: boolean
  onVisibilityChange?: (
    id: string,
    isVisible: boolean,
    legendItemIds?: string[]
  ) => void
  showLegend?: boolean
  hideYAxisLabels?: boolean
  headingImageSlot?: React.ReactNode
  includeAllChains?: boolean
}

export function Chart({
  configAtom,
  showTitle,
  showDescription,
  isVisible = true,
  onVisibilityChange,
  showLegend = true,
  hideYAxisLabels = false,
  headingImageSlot,
  includeAllChains = true,
}: ChartProps) {
  const chartSerieSettings = useAtomValue(configAtom)

  const { series, seriesData } = useSeries(
    chartSerieSettings.configs,
    chartSerieSettings.zoom,
    chartSerieSettings.granularity as GranularityType,
    chartSerieSettings.show_current_period,
    includeAllChains,
    chartSerieSettings?.start,
    chartSerieSettings?.end
  )

  return (
    <div className="flex flex-col gap-3 h-full">
      {(showTitle || showDescription || showLegend) && (
        <div
          className={twMerge(
            "flex flex-col",
            (showDescription || showTitle) && "gap-3"
          )}
        >
          <ChartHeading
            showTitle={showTitle}
            showDescription={showDescription}
            title={chartSerieSettings.title ?? ""}
            description={chartSerieSettings.description ?? ""}
            headingImageSlot={headingImageSlot}
          />
          {showLegend && (
            <Suspense fallback={<LegendPlaceholder />}>
              <Legend
                onVisibilityChange={onVisibilityChange}
                chartSerieSettings={chartSerieSettings}
                series={series}
              />
            </Suspense>
          )}
        </div>
      )}
      <div className="relative min-h-0 flex-grow">
        <BaseChart
          isVisible={isVisible}
          chartSerieSettings={chartSerieSettings}
          series={series}
          seriesData={seriesData}
          hideYAxisLabels={hideYAxisLabels}
        />
      </div>
    </div>
  )
}
